import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

const useNotificationsList = () => {
  const [notifications, setNotifications] = useState([]);
  const notifCount = notifications.filter(n => !n.read).length;
  const fetchRef = useRef(null);
  const { ixBiz } = useSelector(state => state.socketio);

  // Fetches and isolates records strictly mapped to the current tenant business context
  const fetchNotifications = useCallback(() => {
    const storedNotifications =
      JSON.parse(localStorage.getItem('Notifications')) || {};
    const notificationsArray = Object.entries(storedNotifications)
      .filter(([, value]) => value.biz === ixBiz)
      .sort(([a], [b]) => b - a) // Sort chronologically (newest at the top)
      .map(([key, value]) => ({
        ...value,
        notifKey: key // Maps tracking dictionary indices directly down into array items
      }));
    setNotifications(notificationsArray);
  }, [ixBiz]);

  fetchRef.current = fetchNotifications;

  // ✨ ALIGNED LISTENERS SCHEMA
  const LISTENERS = useMemo(
    () => ({
      storage: event => {
        // Synchronize across active cross-tab switches or alternate browser configurations
        if (event.key === 'Notifications') {
          fetchRef.current();
        }
      },
      // Updated from 'CommandsUpdated' to match your utility dispatcher event name
      Notifications: () => {
        fetchRef.current();
      }
    }),
    []
  );

  useEffect(() => {
    fetchNotifications(); // Initial load
    fetchRef.current = fetchNotifications;

    // Dynamically attach tracking listeners across execution environments
    Object.entries(LISTENERS).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    // Clean up connections safely upon unmounting actions
    return () => {
      Object.entries(LISTENERS).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, [fetchNotifications, LISTENERS]);

  // Handle single record item deletion events
  const handleDeleteNotification = useCallback(notifKey => {
    const storedNotifications =
      JSON.parse(localStorage.getItem('Notifications')) || {};

    delete storedNotifications[notifKey];
    localStorage.setItem('Notifications', JSON.stringify(storedNotifications));

    setNotifications(prev => prev.filter(notif => notif.notifKey !== notifKey));

    //  Alert any other hooks or global badges that the global count has dropped
    window.dispatchEvent(new Event('Notifications'));
  }, []);

  // Clear out entire data array context structures
  const clearAllNotifications = useCallback(() => {
    localStorage.removeItem('Notifications');
    setNotifications([]);

    // Dispatch system announcement to completely wipe adjacent cross-tab layouts
    window.dispatchEvent(new Event('Notifications'));
  }, []);

  // Soft updates tracking unread status states
  const markNotificationAsRead = useCallback(notifKey => {
    const storedNotifications =
      JSON.parse(localStorage.getItem('Notifications')) || {};

    if (storedNotifications[notifKey]) {
      storedNotifications[notifKey].read = true;
      localStorage.setItem(
        'Notifications',
        JSON.stringify(storedNotifications)
      );

      setNotifications(prev =>
        prev.map(notif =>
          notif.notifKey === notifKey ? { ...notif, read: true } : notif
        )
      );

      // ✨ Unified global execution thread message dispatcher call
      window.dispatchEvent(new Event('Notifications'));
    }
  }, []);

  return {
    notifications,
    setNotifications,
    handleDeleteNotification,
    clearAllNotifications,
    markNotificationAsRead,
    notifCount
  };
};

export default useNotificationsList;
